import React, { lazy, useCallback, useMemo } from 'react';
import { addReactionToIdea, IdeaPayload, joinIdea, leaveIdea, likeIdea, removeMemberFromIdea, removeReactionFromIdea, unlikeIdea } from '../../services/idea.service';


import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useAuth } from '../../services/useAuth';
import { User } from '../../services/auth.service';
import { useNavigate } from 'react-router-dom';
import { IdeaSummary } from './IdeaSummary';

import { Reactions } from '../Reactions/Reactions';
import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography/Typography';
import Stack from '@mui/material/Stack/Stack';
import Button from '@mui/material/Button/Button';
import Chip from '@mui/material/Chip/Chip';
import { useIdea } from '../../hooks/useIdea';
import { useLiveBoard } from '../../hooks/useLiveSessions';
import Avatar from '@mui/material/Avatar/Avatar';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import Dialog from '@mui/material/Dialog/Dialog';
import DialogTitle from '@mui/material/DialogTitle/DialogTitle';
import DialogContent from '@mui/material/DialogContent/DialogContent';
import DialogContentText from '@mui/material/DialogContentText/DialogContentText';
import DialogActions from '@mui/material/DialogActions/DialogActions';

dayjs.extend(relativeTime);

/** A disabled Join button says why on itself; the tooltip has the full sentence. */
// the idea this person last joined and when: kept outside the card, which the list may remount after the reload
let lastJoined = { ideaId: '', at: 0 };

const joinLabel = (blockedReason: string | null, teamFull: boolean) => {
    if (!blockedReason) {
        return 'Join team';
    }
    if (blockedReason.startsWith('Teams are locked')) {
        return 'Teams locked';
    }
    if (teamFull) {
        return 'Team full';
    }
    return blockedReason;
}

export const IdeaCard = ({
    hackathonId,
    ideaId,
    maxTeamSize,
    isOrganizer,
    onDelete,
    onUnarchive,
    joinBlockedReason,
    showJoin = true,
    editLocked = false,
    teamsLocked = false,
    readOnly = false,
    currentTeam,
    onReloadIdea
}: {
    hackathonId: string,
    ideaId: string,
    isOrganizer: boolean,
    maxTeamSize?: number,
    onUnarchive?: () => void,
    onDelete?: () => void,
    onReloadIdea?: () => void,
    // why the user can't join any team right now (null = they can)
    joinBlockedReason?: string | null,
    // false for judges, who score teams rather than join them
    showJoin?: boolean,
    // true once results are announced: pitches are frozen for everyone but organizers
    editLocked?: boolean,
    // true while voting is open, after results or after the event: members can't leave either
    teamsLocked?: boolean,
    // a past hackathon the user wasn't part of: look, don't touch
    readOnly?: boolean,
    // the team the user is on now, if any; joining this one asks before moving them
    currentTeam?: Pick<IdeaPayload, '_id' | 'title' | 'members'>
}) => {

    const { idea } = useIdea(hackathonId, ideaId);
    // left out of every room's lineup: it won't present, so don't promise that it can
    const board = useLiveBoard(hackathonId);
    const leftOutEverywhere = board.sessions.some(s => s.leftOut.includes(ideaId)) && !board.sessions.some(s => s.ideas.includes(ideaId));
    const reactions = useMemo(() => {
        if (!idea) return [];

        return [...idea.reactions];
    }, [idea])
    const { user } = useAuth();

    const userId = useMemo(() => user ? user._id : '', [user]);

    const navigate = useNavigate();


    const liked = useMemo(() => {
        if (!idea) return false;
        return idea.likedBy.some(l => l._id === userId)
    }, [idea, userId])

    const like = useCallback(() => {
        if (!idea) return false;
        likeIdea(hackathonId, idea._id)
        idea.likedBy.push(user as User)
    }, [idea, hackathonId])

    const unlike = useCallback(() => {
        if (!idea) return false;
        unlikeIdea(hackathonId, idea._id)
        idea.likedBy = idea.likedBy.filter(l => l._id !== userId);
    }, [idea, hackathonId])

    const [processing, setProcessing] = React.useState(false);
    // when this person last joined from this card, and whether they just left it (for Undo)
    const joinedAt = React.useRef(0);
    const [justLeft, setJustLeft] = React.useState(false);
    const [justJoined, setJustJoined] = React.useState(false);

    const isOwner = useMemo(() => {
        if (!idea) return false;
        return idea.createdBy._id === userId;
    }, [idea, userId])

    const isMember = useMemo(() => {
        if (!idea) return false;
        return idea.members.some(m => m._id === userId);
    }, [idea, userId])

    const [joinError, setJoinError] = React.useState('');
    const [confirmSwitch, setConfirmSwitch] = React.useState(false);

    const teamFull = !!idea && !!maxTeamSize && idea.members.length >= maxTeamSize;
    const joinDisabledReason = joinBlockedReason || (teamFull ? 'This team is full' : null);

    if (!idea) {
        return <Box>
            <Typography>Idea not found</Typography>
        </Box>
    }

    const switching = !!currentTeam && currentTeam._id !== idea._id ? currentTeam : undefined;
    const teammates = switching ? switching.members.filter(m => m._id !== userId).map(m => m.username) : [];

    const join = () => {
        setConfirmSwitch(false);
        setProcessing(true);
        setJoinError('');
        setJustLeft(false);
        joinIdea(hackathonId, idea._id, !!switching).then(() => {
            // Join turns into Leave team in the same spot: a double-click must not land on it (run028: Tory joined and left at once)
            joinedAt.current = Date.now();
            lastJoined = { ideaId: idea._id, at: Date.now() };
            setJustJoined(true);
            onReloadIdea && onReloadIdea();
        }, (err) => {
            setJoinError(err?.response?.data?.message || 'Could not join this team');
        }).finally(() => setProcessing(false));
    };

    return (
        <Box id={idea._id}>
            <Stack spacing={2} direction={'column'}>
                {isOwner && <Chip size="small" variant="outlined" color="primary" label="Pitched by you" sx={{ alignSelf: 'flex-start' }} />}
                <IdeaSummary
                    isInTeam={isMember}
                    hackathonId={hackathonId}
                    idea={idea}
                    isOrganizer={isOrganizer}
                    canEdit={isOrganizer || (!editLocked && (isOwner || isMember))}
                    canDelete={isOrganizer || (isOwner && !teamsLocked)}
                    onDelete={onDelete}
                    onUnarchive={onUnarchive}
                    onEdit={onReloadIdea}
                />
                <Box sx={{
                    backgroundColor: 'var(--surface-muted)',
                    borderRadius: '12px',
                    p: 2
                }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="overline" color="text.secondary" sx={{ mr: 0.5 }}>
                                Team {maxTeamSize ? `${idea.members.length}/${maxTeamSize}` : ''}
                            </Typography>
                            {
                                idea.members.map((member) => (
                                    <Chip
                                        key={member._id}
                                        size="small"
                                        avatar={<Avatar>{(member.username || '?').charAt(0).toUpperCase()}</Avatar>}
                                        label={member.username}
                                        sx={{ backgroundColor: 'background.paper' }}
                                        onDelete={isOrganizer ? () => {
                                            // remove member from team
                                            removeMemberFromIdea(hackathonId, idea._id, member.email).then(() => {
                                            }, (err) => {
                                                console.error(err);
                                            });
                                        } : undefined} />
                                ))
                            }
                            {
                                idea.members.length === 0 && <Typography variant="body2" color="text.secondary">
                                    {leftOutEverywhere ? "No members yet. The hosts left it out, so it isn't presenting." : 'No members yet. It can still present and be judged.'}
                                </Typography>
                            }
                        </Stack>
                        {
                            isMember && !teamsLocked && <Button size="small" color={'error'} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }} onClick={() => {
                                if (processing || Date.now() - joinedAt.current < 1500) return;
                                setProcessing(true);
                                leaveIdea(hackathonId, idea._id).then(() => {
                                    setProcessing(false);
                                    // one click to leave, so offer the way back right here
                                    setJustLeft(true);
                                    setJustJoined(false);
                                    onReloadIdea && onReloadIdea();
                                })
                            }}>Leave team</Button>
                        }
                        {
                            justLeft && !isMember && <Stack direction="row" spacing={1} alignItems="center" role="status" sx={{ flexShrink: 0 }}>
                                <Typography variant="body2" color="text.secondary">You left this team.</Typography>
                                <Button size="small" disabled={processing || !!teamsLocked} onClick={join}>Undo</Button>
                            </Stack>
                        }
                        {
                            showJoin && !readOnly && !isMember && !idea.disqualified && <Tooltip title={joinDisabledReason || ''}>
                                {/* a disabled button fires no events, so the span carries the tooltip */}
                                <span>
                                    <Button
                                        size="small"
                                        disabled={!!joinDisabledReason || processing}
                                        variant={'contained'}
                                        startIcon={<GroupAddIcon />}
                                        // several "Join team" buttons sit one under another; the name says which (run029: Patricia joined the wrong one)
                                        aria-label={`Join team: ${idea.title}`}
                                        onClick={() => switching ? setConfirmSwitch(true) : join()}>
                                        {joinLabel(joinDisabledReason, teamFull)}
                                    </Button>
                                </span>
                            </Tooltip>
                        }
                    </Stack>
                    {joinError && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{joinError}</Typography>}
                    {(justJoined || (lastJoined.ideaId === idea._id && Date.now() - lastJoined.at < 60000)) && isMember && <Typography variant="body2" color="success.main" role="status" sx={{ mt: 1 }}>You joined <b>{idea.title}</b>. This is your team now.</Typography>}
                </Box>
                {
                    switching && (
                        <Dialog open={confirmSwitch} onClose={() => setConfirmSwitch(false)} aria-labelledby={`switch-${idea._id}`}>
                            <DialogTitle id={`switch-${idea._id}`}>Switch to {idea.title}?</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    You'll be leaving <b>{switching.title}</b>, {teammates.length
                                        ? <>team members: {teammates.join(', ')}</>
                                        : <>where you're the only member</>}. Are you sure?
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setConfirmSwitch(false)}>Cancel</Button>
                                <Button variant="contained" onClick={join} autoFocus>Switch teams</Button>
                            </DialogActions>
                        </Dialog>
                    )
                }

                <Stack direction={'row'} spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Button
                        disabled={readOnly}
                        color={liked ? 'primary' : 'inherit'}
                        aria-label={`${liked ? 'Unlike' : 'Like'} (${idea.likedBy.length} likes)`}
                        startIcon={liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                        onClick={() => {
                            liked ? unlike() : like()
                        }}>{idea.likedBy.length}</Button>
                    <Button
                        color="inherit"
                        startIcon={<ChatBubbleOutlineIcon />}
                        onClick={() => navigate(`/h/${hackathonId}/idea/${idea._id}`)}>
                        {idea.comments.length} comment{idea.comments.length === 1 ? '' : 's'}
                    </Button>
                {!readOnly && <Reactions
                    reactions={reactions}
                    user={user as User}
                    onAddReaction={(emoji: string) => {
                        addReactionToIdea(hackathonId, idea._id, emoji).then(() => {
                        }, (err) => {
                            console.error(err);
                        })
                    }}
                    onRemoveReaction={(emoji: string) => {
                        removeReactionFromIdea(hackathonId, idea._id, emoji).then(() => {
                        }, (err) => {
                            console.error(err);
                        })
                    }} />}
                </Stack>

            </Stack>
        </Box>
    )
}